export const POSTHOG_EVENTS = {
  LOGIN_SUCCESS: 'login_success',

  PDF_EXPORT: 'pdf_export',

  KARLA_OPEN_BUBBLE: 'karla.open.bubble',
  KARLA_OPEN_TAB: 'karla.open.tab',
  KARLA_BUBBLE_SHOW: 'karla.bubble.show',
  KARLA_MESSAGE_SENT: 'karla.message.sent',
  KARLA_RESPONSE_RECEIVED: 'karla.response.received',
  KARLA_FEEDBACK_GIVEN: 'karla.feedback.given',

  ANALYTICS_FILTERS_APPLY: 'analytics.filters.apply',
  ANALYTICS_RESULTS_FOUND: 'analytics.results.found',
  ANALYTICS_RESULTS_EMPTY: 'analytics.results.empty',
  ANALYTICS_RESULTS_CLICK: 'analytics.results.click',

  ANALYTICS_QUERY_SAVE: 'analytics.query.save',
  ANALYTICS_QUERY_NOTIFICATIONS_ENABLE: 'analytics.query.notifications.enable',
  ANALYTICS_QUERY_NOTIFICATIONS_DISABLE: 'analytics.query.notifications.disable',

  ANALYTICS_TIMELINE_ANNOTATION_ADD: 'analytics.timeline.annotation.add',
  ANALYTICS_TIMELINE_DOWNLOAD: 'analytics.timeline.download',

  ANALYTICS_RULE_CREATE: 'analytics.rule.create',
  ANALYTICS_EXPORT: 'analytics.export',

  ANALYTICS_REQUEST_INTELLIGENCE_CLICK: 'analytics.request_intelligence.click',

  HELP_CENTER_OPEN: 'help_center.open',

  UI_THEME_CURRENT: 'ui.theme.current',
  UI_THEME_TOGGLE: 'ui.theme.toggle',

  SIDEPANEL_OPEN: 'sidepanel.open',
  SIDEPANEL_CLOSE: 'sidepanel.close',
  SIDEPANEL_EXPAND: 'sidepanel.expand',
  SIDEPANEL_ENTITY_NAVIGATE: 'sidepanel.entity.navigate',

  API_DOCS_OPEN: 'api_docs.open',
  NEWS_OPEN: 'news.open',

  SEARCH_GLOBAL_QUERY: 'search.global.query',
  SEARCH_GLOBAL_TAB_SWITCH: 'search.global.tab.switch',
  SEARCH_GLOBAL_RESULT_CLICK: 'search.global.result.click',
  SEARCH_GLOBAL_SIDEPANEL_OPEN: 'search.global.sidepanel.open',

  FILTERS_APPLY: 'filters.apply',
  FILTERS_REMOVE: 'filters.remove',
  FILTERS_CLEAR_ALL: 'filters.clear_all',
  FILTERS_UPDATE: 'filters.update',

  INCIDENTS_ACTION_EXECUTE: 'incidents.action.execute',

  ALERTS_OPEN: 'alerts.open',
  ALERTS_ACTION_EXECUTE: 'alerts.action.execute',
  ALERTS_STATUS_CHANGE: 'alerts.status.change',

  SUPPLY_CHAIN_SUPPLIER_CLICK: 'supply_chain.supplier.click',

  IDENTITY_PANEL_USER_CLICK: 'identity_panel.user.click',
} as const;

export type PosthogEventName = (typeof POSTHOG_EVENTS)[keyof typeof POSTHOG_EVENTS];

export type PosthogEventPayloads = {
  [POSTHOG_EVENTS.LOGIN_SUCCESS]: {
    method: 'password' | 'sso' | 'unknown';
  };

  [POSTHOG_EVENTS.PDF_EXPORT]: {
    pathname: string;
  };

  [POSTHOG_EVENTS.KARLA_OPEN_BUBBLE]: {
    source: 'bubble';
    pathname: string;
  };

  [POSTHOG_EVENTS.KARLA_OPEN_TAB]: {
    source: 'tab';
    pathname: string;
  };

  [POSTHOG_EVENTS.KARLA_BUBBLE_SHOW]: {
    pathname: string;
  };

  [POSTHOG_EVENTS.KARLA_MESSAGE_SENT]: {
    conversation_id: string;
    source: 'bubble' | 'tab';
    message_length: number;
  };

  [POSTHOG_EVENTS.KARLA_RESPONSE_RECEIVED]: {
    conversation_id: string;
    had_error: boolean;
    suggested_action: string | null;
  };

  [POSTHOG_EVENTS.KARLA_FEEDBACK_GIVEN]: {
    rating: 'positive' | 'negative';
    conversation_id: string;
  };

  [POSTHOG_EVENTS.ANALYTICS_FILTERS_APPLY]: {
    query: string;
    preset: string | null;
  };

  [POSTHOG_EVENTS.ANALYTICS_RESULTS_FOUND]: {
    total: number;
    query: string;
    preset: string | null;
  };

  [POSTHOG_EVENTS.ANALYTICS_RESULTS_EMPTY]: {
    query: string;
    preset: string | null;
  };

  [POSTHOG_EVENTS.ANALYTICS_RESULTS_CLICK]: {
    resultId: string;
    position: number;
    pathname: string;
  };

  [POSTHOG_EVENTS.ANALYTICS_QUERY_SAVE]: {
    query: string;
    preset: string | null;
  };

  [POSTHOG_EVENTS.ANALYTICS_QUERY_NOTIFICATIONS_ENABLE]: {
    query: string;
    preset: string | null;
  };

  [POSTHOG_EVENTS.ANALYTICS_QUERY_NOTIFICATIONS_DISABLE]: {
    query: string;
    preset: string | null;
  };

  [POSTHOG_EVENTS.ANALYTICS_TIMELINE_ANNOTATION_ADD]: {
    timestamp: string;
    textLength: number;
  };

  [POSTHOG_EVENTS.ANALYTICS_TIMELINE_DOWNLOAD]: {
    query: string;
    preset: string | null;
    format: string;
  };

  [POSTHOG_EVENTS.ANALYTICS_RULE_CREATE]: {
    query: string | null;
  };

  [POSTHOG_EVENTS.ANALYTICS_EXPORT]: {
    format: 'png';
  };

  [POSTHOG_EVENTS.ANALYTICS_REQUEST_INTELLIGENCE_CLICK]: {
    page: 'raw_issue' | 'intel_issue';
    issueId?: number | string;
  };

  [POSTHOG_EVENTS.HELP_CENTER_OPEN]: {
    source: 'contact_support';
  };

  [POSTHOG_EVENTS.UI_THEME_CURRENT]: {
    theme: 'dark' | 'light';
  };

  [POSTHOG_EVENTS.UI_THEME_TOGGLE]: {
    theme: 'dark' | 'light';
  };

  [POSTHOG_EVENTS.SIDEPANEL_OPEN]: {
    source: string;
    drawerid: string;
  };

  [POSTHOG_EVENTS.SIDEPANEL_CLOSE]: {
    source: string;
    drawerid: string;
  };

  [POSTHOG_EVENTS.SIDEPANEL_EXPAND]: {
    source: string;
    drawerid: string;
  };

  [POSTHOG_EVENTS.SIDEPANEL_ENTITY_NAVIGATE]: {
    source: string;
    drawerid: string;
  };

  [POSTHOG_EVENTS.API_DOCS_OPEN]: {
    source: 'documentation_page';
  };

  [POSTHOG_EVENTS.NEWS_OPEN]: {
    source: 'sidebar';
  };

  [POSTHOG_EVENTS.SEARCH_GLOBAL_QUERY]: {
    query: string;
    activeTab: string | null;
  };

  [POSTHOG_EVENTS.SEARCH_GLOBAL_TAB_SWITCH]: {
    tab: string;
  };

  [POSTHOG_EVENTS.SEARCH_GLOBAL_RESULT_CLICK]: {
    entityId: string;
    entityType: string | null;
  };

  [POSTHOG_EVENTS.SEARCH_GLOBAL_SIDEPANEL_OPEN]: {
    entityId: string;
    entityType: string | null;
  };

  [POSTHOG_EVENTS.FILTERS_APPLY]: {
    filterType: string;
    filterValue: string | null;
    numberOfActiveFilters: number;
  };

  [POSTHOG_EVENTS.FILTERS_REMOVE]: {
    filterType: string;
    filterValue: null;
    numberOfActiveFilters: number;
  };

  [POSTHOG_EVENTS.FILTERS_CLEAR_ALL]: {
    numberOfActiveFilters: number;
  };

  [POSTHOG_EVENTS.FILTERS_UPDATE]: {
    filterType: string;
    filterValue: string | null;
    numberOfActiveFilters: number;
  };

  [POSTHOG_EVENTS.INCIDENTS_ACTION_EXECUTE]: {
    action: 'add_to_watchlist' | 'acknowledge' | 'resolve' | 'request_takedown';
    incidentId: number;
  };

  [POSTHOG_EVENTS.ALERTS_OPEN]: {
    source: 'incident_management_page';
  };

  [POSTHOG_EVENTS.ALERTS_ACTION_EXECUTE]: {
    action:
      | 'mark_as_new'
      | 'mark_as_false_positive'
      | 'mark_as_monitoring'
      | 'create_incident'
      | 'open_incident';
    alertId: string;
  };

  [POSTHOG_EVENTS.ALERTS_STATUS_CHANGE]: {
    previousStatus: string | null;
    newStatus: string;
    alertId: string;
  };

  [POSTHOG_EVENTS.SUPPLY_CHAIN_SUPPLIER_CLICK]: {
    supplierId: number;
    supplierName: string;
  };

  [POSTHOG_EVENTS.IDENTITY_PANEL_USER_CLICK]: {
    userId: string;
    tab: 'employees' | 'customers';
  };
};
